/* eslint-disable no-loop-func */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from 'react';
import { TFunction } from 'i18next';
import { Box, Group, Radio, Stack } from '@mantine/core';

import { CreditOptionsResponse, CreditRequestEmploymentResponse } from 'types';
import { Button, InputLabel, MonthPicker, NumberInput, SectionLabel, Select, Text, YesNoRadioButton } from 'components';
import { AppUtils } from 'utils';
import { AppConstants } from '../../../../../../constants';

const { CREDIT_REQUEST_FORM_PADDING } = AppConstants;

interface CreditRequestEmploymentProps {
  disabled?: boolean;
  form: any;
  options: CreditOptionsResponse;
  t: TFunction;
  userEmploymentForm: any;
  index: number;
  partner: boolean;
  tempEmploymentObject: object;
}

const CreditRequestEmploymentItem = ({
  disabled,
  form,
  options,
  t,
  userEmploymentForm,
  index,
  partner,
  tempEmploymentObject
}: CreditRequestEmploymentProps) => {
  const item = userEmploymentForm.values[index];
  const [thirteenthSalary, setThirteenthSalary] = useState<boolean>(item.thirteenthSalary ?? false);
  const [additionalJob, setAdditionalJob] = useState<boolean>(userEmploymentForm.values[index + 1]);
  
  const errors = useCallback((name: keyof CreditRequestEmploymentResponse) => {
    if (!form || !form.errors) return undefined;
    return form.errors[`employments.${index}.${name}`];
  }, [form?.errors])

  const onThirteenthSalaryChange = useCallback((val: boolean) => {
    setThirteenthSalary(val);
    userEmploymentForm.setFieldValue(`${index}.thirteenthSalary`, val);
  }, []);

  const changeAdditionalJob = useCallback((val: boolean) => {
    setAdditionalJob(val);
    if (val) {
      AppUtils.addNewObject(userEmploymentForm, { ...tempEmploymentObject, creditRequestId: form.values.id, partner: partner });
    } else {
      AppUtils.removeObject(userEmploymentForm, index + 1, { ...tempEmploymentObject, creditRequestId: form.values.id })
    }
  }, []);

  return (
    <Box key={`employment-${index}`} mt={'md'}>
      <SectionLabel label={t(`credit.workplace${partner ? '.partner' : ''}.${index ? 'additional' : 'main'}`)} variant={'green'} mb={16} />
      <Stack pb={CREDIT_REQUEST_FORM_PADDING}>
        <Select
          label={t(`input.professionalSituation.label`)}
          data={options['professionalSituation'].map(el => (
            { value: el, label: t(`input.professionalSituation.options.${el}`) }
          ))}
          key={userEmploymentForm.key(`${index}.professionalSituation`)}
          {...userEmploymentForm.getInputProps(`${index}.professionalSituation`)}
          onChange={(val) => {
            userEmploymentForm.setFieldValue(`${index}.professionalSituation`, val);
            if (val !== 'EMPLOYED') {
              userEmploymentForm.setFieldValue(`${index}.thirteenthSalary`, false);
              userEmploymentForm.setFieldValue(`${index}.bonusReceived`, null);
            }
            if (val !== 'SELF_EMPLOYED_AG') userEmploymentForm.setFieldValue(`${index}.requesterPaysSalary`, null);
            if (val !== 'PENSION') userEmploymentForm.setFieldValue(`${index}.socialHelp`, null);
            if (val !== 'AGENCY') userEmploymentForm.setFieldValue(`${index}.employmentType`, null);
            if (item.professionalSituation !== 'EMPLOYED' && item.professionalSituation !== 'AGENCY') {
              userEmploymentForm.setFieldValue(`${index}.contractStartDate`, null);
              userEmploymentForm.setFieldValue(`${index}.contractEndDate`, null);
              userEmploymentForm.setFieldValue(`${index}.travelingToWork`, null);
            }
          }}
          clearable={true}
          disabled={disabled}
          required={true}
          error={errors('professionalSituation')}
        />

        <Group grow={true}>
          <NumberInput
            label={t(`input.salary.label`)}
            key={userEmploymentForm.key(`${index}.salary`)}
            {...userEmploymentForm.getInputProps(`${index}.salary`)}
            min={0}
            rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
            rightSectionWidth={40}
            disabled={disabled}
            error={errors('salary')}
            required={true}
          />
          {item.professionalSituation === 'EMPLOYED' ?
            <Stack gap={0} pb={userEmploymentForm.errors?.salary ? 22 : 0}>
              <InputLabel label={t(`input.thirteenthSalary.label`)} />
              <Group grow={true} pb={5}>
                <Button
                  onClick={() => onThirteenthSalaryChange(false)}
                  bg={thirteenthSalary === true ? '#e8eaf0' : '#439425'}
                  c={thirteenthSalary === false ? '#e8eaf0' : '#439425'}
                  style={{ borderRadius: '12px', height: '44px' }}
                >12</Button>
                <Button
                  onClick={() => onThirteenthSalaryChange(true)}
                  bg={thirteenthSalary === false ? '#e8eaf0' : '#439425'}
                  c={thirteenthSalary === true ? '#e8eaf0' : '#439425'}
                  style={{ borderRadius: '12px', height: '44px' }}
                >13</Button>
              </Group>
            </Stack>
          : undefined}
        </Group>
        {item.professionalSituation === 'EMPLOYED' ?
          <YesNoRadioButton
            label={t(`input.bonusReceived.label`)}
            name={`${index}.bonusReceived`}
            form={userEmploymentForm}
            t={t}
            disabled={disabled}
          />
          :undefined
        }
        {item.professionalSituation === 'SELF_EMPLOYED_AG' ?
          <YesNoRadioButton
            label={t(`input.requesterPaysSalary.label`)}
            name={`${index}.requesterPaysSalary`}
            form={userEmploymentForm}
            t={t}
            disabled={disabled}
          />
        : undefined}
        {item.professionalSituation === 'PENSION' ?
          <YesNoRadioButton
            label={t(`input.socialHelp.label`)}
            name={`${index}.socialHelp`}
            form={userEmploymentForm}
            t={t}
            disabled={disabled}
          />
        : undefined}
        {(item.professionalSituation === 'EMPLOYED' || item.professionalSituation === 'AGENCY') ?
          <>
            {item.professionalSituation === 'AGENCY' &&
              <Select
                label={t(`input.employmentType.label`)}
                data={options['employmentType'].map(el => (
                  { value: el, label: t(`input.employmentType.options.${el}`) }
                ))}
                key={userEmploymentForm.key(`${index}.employmentType`)}
                {...userEmploymentForm.getInputProps(`${index}.employmentType`)}
                clearable={true}
                disabled={disabled}
              />
            }
            <MonthPicker
              label={t(`input.contractStartDate.label`)}
              key={userEmploymentForm.key(`${index}.contractStartDate`)}
              {...userEmploymentForm.getInputProps(`${index}.contractStartDate`)}
              disabled={disabled}
              maxDate={new Date()}
              required={true}
              error={errors('contractStartDate')}
            />
            {item.employmentType === 'DEFINED' ?
              <MonthPicker
                label={t(`input.contractEndDate.label`)}
                key={userEmploymentForm.key(`${index}.contractEndDate`)}
                {...userEmploymentForm.getInputProps(`${index}.contractEndDate`)}
                disabled={disabled}
                minDate={new Date()}
              />
            : undefined}
            <Select
              label={t(`input.travelingToWork.label`)}
              data={options['travelingToWork'].map(el => (
                { value: el, label: t(`input.travelingToWork.options.${el}`) }
              ))}
              key={userEmploymentForm.key(`${index}.travelingToWork`)}
              {...userEmploymentForm.getInputProps(`${index}.travelingToWork`)}
              clearable={true}
              disabled={disabled}
            />
          </>
        : undefined}
        {!disabled ?
          <Box mb={6}>
            <InputLabel label={t(`action.addEmployment${partner ? 'Partner' : ''}`)} />
            <Group>
              <Radio color={'#75AD53'} checked={additionalJob} onChange={() => changeAdditionalJob(true)} label={t('input.radio.true')} disabled={disabled} />
              <Radio color={'#75AD53'} checked={!additionalJob} onChange={() => changeAdditionalJob(false)} label={t('input.radio.false')} disabled={disabled} />
            </Group>
          </Box>
        : undefined}
      </Stack>
    </Box>
  )
}

export default CreditRequestEmploymentItem;